import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { retryPolicy } from '../api/retry.js';
import { useAuth } from './auth.store.js';
/**
 * The caller's permissions, from the server.
 *
 * `GET /auth/me` returns the contract-typed `AccessScope`, which is the only
 * honest answer to "what may this person do". The alternative the PWA used —
 * `user.role !== 'SALES' && user.role !== 'SPG'` — is a guess that goes stale
 * the moment an administrator edits the role/permission matrix, and it cannot
 * express the region entitlement at all.
 *
 * Since ADR-0006 that is not merely unwise, it is unsound. Roles are rows an
 * administrator creates, renames and deletes at runtime: the string `'SALES'`
 * may name a role that does not exist on this installation, and a role named
 * something nobody has ever seen may hold exactly the permissions the branch
 * was trying to describe. `can()` is the only check that means anything.
 *
 * This still decides what to OFFER, not what to allow. A hidden menu item is not
 * access control (TSP 2.2.2); every endpoint behind one is guarded by the global
 * AuthGuard and row-scoped independently of what the client renders.
 *
 * Cached for the session: the matrix changes rarely, and re-fetching it on every
 * navigation would put a request in front of every sidebar render.
 */
export function useAccessScope() {
    const accessToken = useAuth((state) => state.accessToken);
    return useQuery({
        queryKey: ['access-scope'],
        queryFn: () => api.get('/auth/me'),
        enabled: accessToken !== null,
        staleTime: 5 * 60_000,
        /*
         * THE FIRST QUERY OF EVERY SESSION, and the one whose failure is loudest.
         *
         * Both applications gate on it: the console renders "Gagal memuat izin
         * akses" and offers nothing else, and the staff app cannot decide what a
         * user may see. `retry: 1` gave up after two attempts, so an api restart —
         * tens of seconds, and there is one instance in production — landed a
         * manager on that dead end for something that fixes itself.
         *
         * Three attempts rather than the console's six. This one is shared with a
         * handset, where the same "no response" is usually a dead zone rather than a
         * restart, and the staff client's retry budget is a battery decision
         * (`_layout.tsx`). Three covers a restart without arguing with that.
         */
        retry: retryPolicy(3),
    });
}
/**
 * Permission test that treats "scope not loaded yet" as "no".
 *
 * Failing closed matters here: the opposite default would flash administrative
 * navigation to every user for the duration of one request.
 */
export function can(scope, permission) {
    return scope?.permissions.includes(permission) ?? false;
}
/** True when the scope grants every one of `permissions`. */
export function canAll(scope, permissions) {
    return permissions.every((permission) => can(scope, permission));
}
/** True when the scope grants at least one of `permissions`. */
export function canAny(scope, permissions) {
    return permissions.some((permission) => can(scope, permission));
}
//# sourceMappingURL=use-access-scope.js.map